import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Loader2, Search, Download, ArrowLeft, LayoutGrid, CheckSquare, XSquare,
} from "lucide-react";

const API = `${API_BASE}/api`;

// Human-facing labels for the category dropdown. Order mirrors the deck flow
// on the admin side so the picker reads the same way as the admin console.
const CATEGORY_LABELS = {
  intro: "Intro / Recommendation",
  seo_aeo: "SEO / AEO",
  prospectid: "ProspectID",
  prospectmax: "ProspectMax",
  website: "Website",
  pricing: "Pricing",
  closing: "Closing",
  other: "Other",
};

export default function SlideLibraryPickerPage() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  // Order-preserving selection: array of ids in the order the user picked them
  const [selected, setSelected] = useState([]);
  const [filename, setFilename] = useState("my_slides");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // active_only=true — reps only see slides admins have marked active
        const r = await axios.get(`${API}/proposal-slide-library`);
        setSlides(r.data || []);
      } catch (e) {
        toast.error("Failed to load slide library");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return slides.filter((s) => {
      if (category !== "all" && s.category !== category) return false;
      if (q && !(s.title || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [slides, search, category]);

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAllVisible = () => {
    const ids = filtered.map((s) => s.id);
    // De-dup: keep existing order, append new ids from the filtered view
    setSelected((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
  };

  const clearSelection = () => setSelected([]);

  const download = async () => {
    if (!selected.length) {
      toast.error("Pick at least one slide first");
      return;
    }
    setDownloading(true);
    try {
      const r = await axios.post(
        `${API}/proposal-slide-library/export`,
        { slide_ids: selected, filename: filename || "my_slides" },
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (filename || "my_slides").replace(/[^A-Za-z0-9_-]+/g, "_") || "my_slides";
      a.download = `${safe}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.length} slide${selected.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  // Distinct categories that actually contain slides — hides empty dropdown items
  const availableCategories = useMemo(() => {
    const set = new Set();
    slides.forEach((s) => set.add(s.category || "other"));
    return Array.from(set);
  }, [slides]);

  return (
    <div className="space-y-6" data-testid="slide-library-picker">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
            <Link to="/resources" data-testid="back-to-resources">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to Resources
            </Link>
          </Button>
          <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2">
            <LayoutGrid className="h-7 w-7 text-indigo-600" />
            Slide Library
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Pick individual slides from every master deck and download them as a single custom PPTX.
            Use it to build a one-off pitch, share a specific product explainer, or drop a slide
            into an email.
          </p>
        </div>
      </div>

      {/* Sticky download bar — visible as soon as the user picks one slide */}
      {selected.length > 0 && (
        <Card className="border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50 sticky top-2 z-10 shadow-md"
              data-testid="slide-library-download-bar">
          <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white">
                {selected.length} selected
              </Badge>
              <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
                <XSquare className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Filename:</span>
                <Input
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  className="h-8 w-48"
                  data-testid="slide-library-filename"
                  placeholder="my_slides"
                />
                <span className="text-xs text-muted-foreground">.pptx</span>
              </div>
            </div>
            <Button
              onClick={download}
              disabled={downloading}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="download-selected-btn"
            >
              {downloading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Building…</>
              ) : (
                <><Download className="h-4 w-4 mr-2" /> Download {selected.length} slide{selected.length === 1 ? "" : "s"}</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title…"
            className="pl-9"
            data-testid="slide-library-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[220px]" data-testid="slide-library-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {availableCategories.map((c) => (
              <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={selectAllVisible} data-testid="select-all-visible-btn">
          <CheckSquare className="h-3.5 w-3.5 mr-1" /> Select all visible
        </Button>
        <div className="text-xs text-muted-foreground ml-auto">
          {filtered.length} of {slides.length} shown
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center text-muted-foreground">
            <LayoutGrid className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No slides match your filters.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
             data-testid="slide-library-grid">
          {filtered.map((s) => {
            const isSelected = selected.includes(s.id);
            const selIdx = isSelected ? selected.indexOf(s.id) + 1 : null;
            return (
              <Card
                key={s.id}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={() => toggle(s.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(s.id);
                  }
                }}
                className={`cursor-pointer transition group overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                  isSelected
                    ? "ring-2 ring-indigo-500 border-indigo-300 shadow-md"
                    : "border hover:shadow-md hover:border-indigo-200"
                }`}
                data-testid={`slide-card-${s.id}`}
              >
                <div className="relative bg-zinc-100 aspect-video">
                  {s.has_thumbnail ? (
                    <img
                      src={`${API}/proposal-slide-library/${s.id}/thumbnail`}
                      alt={s.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                      No preview
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(s.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-white/95 border-white shadow"
                      data-testid={`slide-check-${s.id}`}
                    />
                  </div>
                  {selIdx && (
                    <div className="absolute top-2 right-2 bg-indigo-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow">
                      {selIdx}
                    </div>
                  )}
                </div>
                <CardContent className="p-3 space-y-1.5">
                  <p className="text-xs font-semibold line-clamp-2" title={s.title}>
                    {s.title || `Slide ${s.source_slide_idx}`}
                  </p>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="secondary" className="text-[10px] font-normal">
                      {CATEGORY_LABELS[s.category] || s.category || "other"}
                    </Badge>
                    {(s.product_tags || []).slice(0, 2).map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px] font-normal">
                        {t.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
